import type * as maplibregl from "maplibre-gl";
import { PEAK_ICON_DARK_IMAGE_ID } from "./poiIcons";

// Тёмная тема карты. Светлый стиль (basemapLayers.ts) остаётся источником
// правды: тёмные значения - это переопределения paint-свойств слоёв, которые
// накладываются живьём (setPaintProperty), без пересоздания стиля. Исходное
// значение запоминается при первом наложении и возвращается обратно при
// переходе на светлую тему.
//
// Палитра - холодный графит с фирменным зелёным: фон и вода темнее суши,
// дороги и тропы светлее фона, подписи светлые с тёмным ореолом.

const BG = "#13171c";
const HALO = "#13171c";
const TEXT = "#d9dee5";

type Paint = Record<string, unknown>;

// layer id -> { paint property -> значение в тёмной теме }
const DARK: Record<string, Paint> = {
  "basemap-background": { "background-color": BG },
  "landcover-ground": {
    "fill-color": [
      "match", ["get", "kind"],
      "water", "#1c3a4a",
      "forest", "#1d3322",
      "scrub", "#25392a",
      "glacier", "#33444f",
      "scree", "#2d2b28",
      "blockfield", "#252320",
      "transparent",
    ],
  },
  "landcover-water": {
    "fill-color": [
      "match", ["get", "kind"],
      "water", "#1c3a4a",
      "forest", "#1d3322",
      "scrub", "#25392a",
      "glacier", "#33444f",
      "scree", "#2d2b28",
      "blockfield", "#252320",
      "transparent",
    ],
  },
  "landcover-ground-outline": { "line-color": "#47683f" },
  hillshade: {
    "hillshade-shadow-color": "rgba(0, 0, 0, 0.55)",
    "hillshade-highlight-color": "rgba(255, 255, 255, 0.10)",
    "hillshade-accent-color": "rgba(0, 0, 0, 0.30)",
  },
  "waterways-line": { "line-color": ["match", ["get", "waterway"], "river", "#3d88ab", "#2f6f8c"] },
  "waterways-line-intermittent": { "line-color": ["match", ["get", "waterway"], "river", "#3d88ab", "#2f6f8c"] },
  "contours-line": { "line-color": "#64503f" },
  "contours-index": { "line-color": "#80664c" },
  "contours-label": { "text-color": "#a38c76", "text-halo-color": HALO },
  "ridges-line": { "line-color": "#c58b63" },
  "buildings-fill": { "fill-color": "#444a52" },
  "buildings-dam": { "fill-color": "#5d656e" },
  "buildings-outline-dam": { "line-color": "#9aa3ab" },
  "buildings-outline": { "line-color": "#7b828a" },
  "roads-casing-track": { "line-color": "#0b0d10" },
  "roads-casing": { "line-color": "#0b0d10" },
  "roads-line": { "line-color": "#8e97a1" },
  "power-lines-line": { "line-color": "#6b7078" },
  "trails-line": { "line-color": "#eceff3" },
  "pois-peak": { "text-color": TEXT, "text-halo-color": HALO },
  "pois-pass": { "text-color": TEXT, "text-halo-color": HALO, "icon-color": "#e7e2d9" },
  "pois-peak-admin": { "text-color": TEXT, "text-halo-color": HALO },
  "pois-pass-admin": { "text-color": TEXT, "text-halo-color": HALO, "icon-color": "#e7e2d9" },
  "pois-power-tower": { "icon-color": "#e7e2d9" },
  "pois-label": { "text-color": TEXT, "text-halo-color": HALO },
  "buildings-label": { "text-color": "#c9bfb2", "text-halo-color": HALO },
  "buildings-label-admin": { "text-color": "#c9bfb2", "text-halo-color": HALO },
  "waterways-label": { "text-halo-color": HALO },
  "landcover-water-label": { "text-halo-color": HALO },
  "places-label": { "text-color": "#e3e1dc", "text-halo-color": HALO },
  "boundaries-label": { "text-color": "#ff9a8f", "text-halo-color": HALO },
};

// То же для layout-свойств (иконка вершины - растровая, перекрасить нельзя,
// поэтому в тёмной теме подставляется её тёмный вариант).
const DARK_LAYOUT: Record<string, Paint> = {
  "pois-peak": { "icon-image": PEAK_ICON_DARK_IMAGE_ID },
  "pois-peak-admin": { "icon-image": PEAK_ICON_DARK_IMAGE_ID },
};

// layer id -> property -> исходное (светлое) значение
const originals = new WeakMap<maplibregl.Map, Map<string, unknown>>();

const keyOf = (layer: string, prop: string) => `${layer}\u0000${prop}`;

export const applyMapTheme = (map: maplibregl.Map, dark: boolean): void => {
  let saved = originals.get(map);
  if (!saved) {
    saved = new Map();
    originals.set(map, saved);
  }
  for (const [layer, paint] of Object.entries(DARK)) {
    if (!map.getLayer(layer)) continue;
    for (const [prop, darkValue] of Object.entries(paint)) {
      const k = keyOf(layer, prop);
      if (!saved.has(k)) saved.set(k, map.getPaintProperty(layer, prop as never));
      // setPaintProperty(undefined) возвращает свойство к значению по умолчанию.
      map.setPaintProperty(layer, prop as never, (dark ? darkValue : saved.get(k)) as never);
    }
  }
  for (const [layer, layout] of Object.entries(DARK_LAYOUT)) {
    if (!map.getLayer(layer)) continue;
    for (const [prop, darkValue] of Object.entries(layout)) {
      const k = `layout\u0000${keyOf(layer, prop)}`;
      if (!saved.has(k)) saved.set(k, map.getLayoutProperty(layer, prop as never));
      map.setLayoutProperty(layer, prop as never, (dark ? darkValue : saved.get(k)) as never);
    }
  }
};
